import { format, parseISO, getYear } from 'date-fns';

/**
 * Utility function to parse date strings as local dates (fixes timezone offset issue)
 */
export const parseLocalDate = (dateString) => {
  if (!dateString) return new Date();
  // Append T00:00:00 to ensure it's parsed as local time, not UTC
  const localDateString = dateString.includes('T') ? dateString : `${dateString}T00:00:00`;
  return new Date(localDateString);
};

/**
 * Determines if a date is from a previous academic/admissions cycle
 * Returns true if the date is from an earlier year than the current academic year
 */
export const isPreviousYearDate = (dateString) => {
  if (!dateString) return false;
  
  const date = parseLocalDate(dateString);
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-based, August = 7
  const dateYear = getYear(date);
  const dateMonth = date.getMonth();
  
  // Determine current admissions cycle
  // The 2025-2026 cycle runs from Aug 2025 through July 2026
  // If we're in Aug 2025 or later, we're in the 2025-2026 cycle
  // Dates from 2025 (like Nov 1, 2025) are CURRENT, not previous
  
  // For the 2025-2026 cycle (starting Aug 2025):
  // - Valid dates: Aug 2025 through July 2026
  // - Previous cycle dates: anything before Aug 2025
  
  if (currentMonth >= 7) { // August or later
    // We're in the new academic year cycle
    // Current cycle includes this year and next year's spring
    if (dateYear < currentYear) {
      return true;
    }
    if (dateYear === currentYear && dateMonth < 7) {
      // Date is from earlier in this calendar year (before August)
      return true;
    }
    // Dates from this fall/winter/spring are current
    return false;
  } else {
    // We're before August (spring semester)
    // Current cycle started last August
    if (dateYear < currentYear - 1) {
      return true;
    }
    if (dateYear === currentYear - 1 && dateMonth < 7) {
      return true;
    }
    // Dates from last fall through this spring are current
    return false;
  }
};

/**
 * Gets a formatted date string with an estimated indicator if needed
 */
export const formatDateWithEstimate = (dateString, options = {}) => {
  if (!dateString) return '';
  
  const date = parseLocalDate(dateString);
  const isEstimate = isPreviousYearDate(dateString);
  
  const formattedDate = format(date, options.format || 'MMM d, yyyy');
  
  if (isEstimate) {
    return `${formattedDate} (est.)`;
  }
  
  return formattedDate;
};

/**
 * Creates a date estimation notice component props
 */
export const getDateEstimationProps = (dateString) => {
  const isEstimate = isPreviousYearDate(dateString);
  
  if (!isEstimate) {
    return { isEstimate: false };
  }
  
  return {
    isEstimate: true,
    tooltip: 'This date is from a previous year\'s admissions cycle and should be considered an estimate. Check the college\'s website for updated dates.',
    className: 'text-amber-600',
    badge: 'Estimated'
  };
};

/**
 * Creates a warning message for dates that need updating
 */
export const getDateUpdateWarning = (dates) => {
  const estimatedDates = dates.filter(date => isPreviousYearDate(date));
  
  if (estimatedDates.length === 0) {
    return null;
  }
  
  return {
    count: estimatedDates.length,
    message: `${estimatedDates.length} deadline${estimatedDates.length !== 1 ? 's' : ''} may need updating. These dates are from previous admissions cycles and should be verified with each college's current requirements.`
  };
};

/**
 * Intelligently checks if a college has outdated dates
 * Prioritizes global_colleges data over college_journey_colleges data
 * @param {Object} college - The college object from college_journey_colleges
 * @param {Object} globalData - Optional global college data
 * @returns {boolean} - True if dates need updating
 */
export const collegeHasOutdatedDates = (college, globalData = null) => {
  const deadlineFields = [
    'ed_deadline', 'ed2_deadline', 'ea_deadline', 'rd_deadline',
    'finaid_deadline', 'css_deadline', 'merit_deadline', 'honors_deadline'
  ];
  
  let hasOutdatedDates = false;
  
  deadlineFields.forEach(field => {
    const journeyDate = college[field];
    const globalDate = globalData?.[field];
    
    if (!journeyDate && !globalDate) return; // No date in either table
    
    // Prioritize global data if available
    const dateToCheck = globalDate || journeyDate;
    
    if (dateToCheck && isPreviousYearDate(dateToCheck)) {
      hasOutdatedDates = true;
    }
  });
  return hasOutdatedDates;
};